/**
 * @file
 * Image Gallery Inline behavior.
 *
 * @description
 * Manages the inline image gallery component:
 *  – Shows only the first N images in the inline grid (responsive).
 *  – Opens a full-screen overlay grid via the "See all" button.
 *  – Implements focus trapping inside the overlay dialog.
 *  – Clicking an image (inline or overlay) opens the PhotoSwipe lightbox
 *    at the correct index by dispatching a click on the matching
 *    `a.photoswipe` anchor inside the inline grid.
 */

((Drupal, once) => {
  ('use strict');

  type GalleryInlineElement = HTMLElement & {
    _tpGalleryInlineCleanup?: () => void;
  };

  /** Number of images visible inline per breakpoint. */
  const VISIBLE_DESKTOP = 5;
  const VISIBLE_TABLET = 4;
  const VISIBLE_MOBILE = 1;
  const BP_XL = 1280;
  const BP_MD = 768;

  /**
   * Returns the number of images that should be visible inline based on
   * the current viewport width.
   */
  function getVisibleCount(): number {
    const w = window.innerWidth;
    if (w >= BP_XL) return VISIBLE_DESKTOP;
    if (w >= BP_MD) return VISIBLE_TABLET;
    return VISIBLE_MOBILE;
  }

  /**
   * Returns all focusable elements inside a container.
   */
  function getFocusableElements(container: HTMLElement): HTMLElement[] {
    return Array.from(
      container.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input:not([type="hidden"]), select, [tabindex]:not([tabindex="-1"])'
      )
    );
  }

  /**
   * Announces status changes to assistive technologies when available.
   */
  function announce(message: string): void {
    if (Drupal.announce) {
      Drupal.announce(message);
    }
  }

  /**
   * Image Gallery Inline Drupal behavior.
   *
   * @description
   * Initialises every `[data-gallery-inline]` element on the page.
   * Handles responsive visibility of inline items, the overlay modal
   * (open/close, focus trap, scroll lock), and PhotoSwipe lightbox
   * integration at the correct image index.
   */

  Drupal.behaviors.tpGalleryInline = {
    attach(context: Document | HTMLElement) {
      const galleries = once('tp-gallery-inline', '[data-gallery-inline]', context);

      galleries.forEach((el) => {
        const gallery = el as GalleryInlineElement;
        const grid = gallery.querySelector<HTMLElement>('.gallery-inline__grid');
        const seeAllBtn = gallery.querySelector<HTMLElement>('.gallery-inline__see-all-btn');
        const overlay = gallery.querySelector<HTMLElement>('[data-gallery-overlay]');
        const overlayGrid = overlay?.querySelector<HTMLElement>('.gallery-inline__overlay-grid');
        const closeBtn = overlay?.querySelector<HTMLElement>('.gallery-inline__overlay-close');
        const totalCount = Number(gallery.dataset.totalCount ?? '0');

        if (!grid) return;

        const items = Array.from(grid.querySelectorAll<HTMLElement>('.gallery-inline__item'));

        // Track whether the overlay is currently closing to prevent interaction.
        let isClosing = false;

        // ---------------------------------------------------------------
        // 1. Responsive inline visibility
        // ---------------------------------------------------------------

        /** Show/hide items based on viewport. */
        function applyVisibility(): void {
          const visible = getVisibleCount();

          items.forEach((item, index) => {
            item.classList.toggle('gallery-inline__item--hidden', index >= visible);
          });

          if (seeAllBtn) {
            seeAllBtn.hidden = totalCount <= visible;
          }
        }

        applyVisibility();

        // Debounced resize handler
        let resizeTimer: ReturnType<typeof setTimeout>;
        const onResize = () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(applyVisibility, 150);
        };
        window.addEventListener('resize', onResize);

        // ---------------------------------------------------------------
        // 2. PhotoSwipe lightbox — open at correct index
        // ---------------------------------------------------------------

        /**
         * Programmatically open the PhotoSwipe lightbox at a given index.
         * We find the Nth `a.photoswipe` anchor inside the inline grid and
         * dispatch a click so the drupal/photoswipe module handles the rest.
         */
        function openLightboxAt(index: number): void {
          const anchors = grid!.querySelectorAll<HTMLAnchorElement>('a.photoswipe');
          const anchor = anchors[index];
          if (anchor) {
            anchor.click();
          }
        }

        // Inline grid image clicks — determine index and open lightbox.
        items.forEach((item, index) => {
          item.addEventListener('click', (e: MouseEvent) => {
            // Let native PhotoSwipe anchor handle it if the click target is
            // already the anchor or inside it.
            const target = e.target as HTMLElement;
            if (target.closest('a.photoswipe')) return;

            e.preventDefault();
            openLightboxAt(index);
          });
        });

        // ---------------------------------------------------------------
        // 3. Overlay grid population
        // ---------------------------------------------------------------

        let isOverlayPopulated = false;

        /**
         * Populates the overlay grid on first open.
         *
         * @description
         * Deferring clones keeps hidden gallery images out of the initial
         * rendering path. Cloned images remain lazy-loaded so below-fold
         * overlay thumbnails are fetched only as needed.
         */
        function populateOverlayGrid(): void {
          if (!overlay || !overlayGrid || isOverlayPopulated) return;

          items.forEach((item, index) => {
            const clone = item.cloneNode(true) as HTMLElement;
            clone.classList.remove('gallery-inline__item--hidden');
            clone.classList.remove('gallery-inline__item');
            clone.classList.add('gallery-inline__overlay-item');
            clone.setAttribute('role', 'listitem');

            // Remove any `once` data attributes to avoid conflicts.
            clone.removeAttribute('data-once');

            clone.querySelectorAll('img').forEach((img) => {
              img.setAttribute('loading', 'lazy');
              img.setAttribute('decoding', 'async');
            });

            // Replace inner anchors with non-navigating buttons to avoid
            // keyboard users accidentally following the PhotoSwipe href.
            clone.querySelectorAll('a').forEach((a) => {
              a.setAttribute('role', 'button');
              a.removeAttribute('href');
              a.setAttribute('tabindex', '0');
            });

            // Override click to open lightbox at correct index.
            clone.addEventListener('click', (e: MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              if (isClosing) return;
              closeOverlay(false);
              // Small delay so the overlay closes before PhotoSwipe opens.
              requestAnimationFrame(() => {
                openLightboxAt(index);
              });
            });

            // Support keyboard activation (Enter/Space) on overlay items.
            clone.addEventListener('keydown', (e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                if (isClosing) return;
                closeOverlay(false);
                requestAnimationFrame(() => {
                  openLightboxAt(index);
                });
              }
            });

            overlayGrid.appendChild(clone);
          });

          isOverlayPopulated = true;
        }

        // ---------------------------------------------------------------
        // 4. Overlay open / close
        // ---------------------------------------------------------------

        let previousFocus: HTMLElement | null = null;

        function openOverlay(): void {
          if (!overlay || isClosing) return;
          previousFocus = document.activeElement as HTMLElement;
          populateOverlayGrid();
          overlay.removeAttribute('hidden');
          document.body.classList.add('gallery-inline-overlay-open');

          // Force reflow before adding the open class for transition.
          void overlay.offsetHeight;
          overlay.classList.add('gallery-inline__overlay--open');

          // Move focus to close button.
          closeBtn?.focus();

          // Announce to screen readers.
          announce(Drupal.t('Gallery overlay opened. Showing all images.'));

          // Attach event listeners.
          document.addEventListener('keydown', onOverlayKeydown);
        }

        function closeOverlay(restoreFocus = true): void {
          if (!overlay || isClosing) return;
          isClosing = true;

          overlay.classList.remove('gallery-inline__overlay--open');
          document.body.classList.remove('gallery-inline-overlay-open');

          // Detach keyboard handler immediately to prevent re-entry.
          document.removeEventListener('keydown', onOverlayKeydown);

          // Wait for transition before hiding.
          const finalise = () => {
            overlay.setAttribute('hidden', '');
            isClosing = false;
            // Restore focus after hidden is set, unless handing off to PhotoSwipe.
            if (restoreFocus) {
              previousFocus?.focus();
            }
          };

          const onTransitionEnd = () => {
            overlay.removeEventListener('transitionend', onTransitionEnd);
            finalise();
          };
          overlay.addEventListener('transitionend', onTransitionEnd);

          // Fallback in case transitionend doesn't fire.
          setTimeout(() => {
            if (!overlay.hasAttribute('hidden')) {
              finalise();
            }
          }, 500);

          // Announce to screen readers.
          announce(Drupal.t('Gallery overlay closed.'));
        }

        // "See all" button handler.
        seeAllBtn?.addEventListener('click', openOverlay);

        // Close button handler.
        closeBtn?.addEventListener('click', () => closeOverlay());

        // ---------------------------------------------------------------
        // 5. Overlay keyboard handling & focus trap
        // ---------------------------------------------------------------

        function onOverlayKeydown(e: KeyboardEvent): void {
          if (!overlay) return;

          switch (e.key) {
            case 'Escape':
              e.preventDefault();
              closeOverlay();
              break;

            case 'Tab': {
              // Focus trap: wrap focus within the overlay.
              const focusable = getFocusableElements(overlay);
              if (focusable.length === 0) {
                e.preventDefault();
                return;
              }

              const first = focusable[0];
              const last = focusable[focusable.length - 1];

              if (e.shiftKey) {
                if (document.activeElement === first) {
                  e.preventDefault();
                  last.focus();
                }
              } else {
                if (document.activeElement === last) {
                  e.preventDefault();
                  first.focus();
                }
              }
              break;
            }
          }
        }

        gallery._tpGalleryInlineCleanup = () => {
          clearTimeout(resizeTimer);
          window.removeEventListener('resize', onResize);
          document.removeEventListener('keydown', onOverlayKeydown);

          if (overlay && !overlay.hasAttribute('hidden')) {
            overlay.classList.remove('gallery-inline__overlay--open');
            overlay.setAttribute('hidden', '');
          }

          if (!document.querySelector('[data-gallery-overlay]:not([hidden])')) {
            document.body.classList.remove('gallery-inline-overlay-open');
          }

          delete gallery._tpGalleryInlineCleanup;
        };
      });
    },

    /**
     * Cleanup on detach (AJAX compatibility).
     */
    detach(_context: Document | HTMLElement, _settings: unknown, trigger: string) {
      if (trigger === 'unload') {
        const galleries = new Set<GalleryInlineElement>();

        if (_context instanceof HTMLElement && _context.matches('[data-gallery-inline]')) {
          galleries.add(_context as GalleryInlineElement);
        }

        const root = _context instanceof HTMLElement ? _context : document;
        root.querySelectorAll<GalleryInlineElement>('[data-gallery-inline]').forEach((gallery) => {
          galleries.add(gallery);
        });

        galleries.forEach((gallery) => {
          gallery._tpGalleryInlineCleanup?.();
        });
      }
    },
  };
})(Drupal, once);
